




































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import ContentStateComponent from "@/components/common/ContentState.vue";
import ClientPartnerFieldsDialogComponent, { PartnerDialogResult } from "@/components/admin/clients/partners/ClientPartnerFieldsDialog.vue";
import ClientPartnerCredentialsDialogComponent from "@/components/admin/clients/partners/ClientPartnerCredentialsDialog.vue";
import ClientPartnerPasswordDialogComponent from "@/components/admin/clients/partners/ClientPartnerPasswordDialog.vue";
import ClientPartnerLinkDialogComponent from "@/components/admin/clients/partners/ClientPartnerLinkDialog.vue";
import ClientPartnerContactsDialogComponent from "@/components/admin/clients/partners/ClientPartnerContactsDialog.vue";
import ClientPartnerPackagesComponent from "@/components/admin/clients/partners/ClientPartnerPackages.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import {
  ClientPartner,
  ClientPartnerLinkClient,
  GoLiveCheck,
  PARTNER_ACCEPTS_FIELDS,
  PARTNER_ENDPOINT_FIELDS,
  PartnerChangeGroup,
  PartnerContact,
} from "@/data/models/ClientPartner";
import { formatDateToDayMonthYear, formatDateToHoursAndMinuteString } from "@/utils/DateHelper";
import { COLOR_ALL_DEVICES_EDIT_ICON } from "@/utils/colors";
import { getClientPartner, resetClientPartnerPassword, setClientPartnerLink } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import router from "@/router/router";
import { setClientPageToolbarTitle } from "@/utils/ClientAdministrationTitle";
import { DEFAULT_ENDPOINTS, partnerErrorKey, partnerSystemLabelKey } from "@/utils/ClientPartners";
import { ComponentName } from "@/utils/RouteGuards";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";

const PARTNERS = "admin.clients.partners";

interface PartnerCard {
  group: PartnerChangeGroup;
  title: string;
  description?: string;
  lines: Array<{ label: string; value: string }>;
}

@Component({
  name: "ClientPartnerDetail",
  components: {
    "content-state": ContentStateComponent,
    "client-partner-fields-dialog": ClientPartnerFieldsDialogComponent,
    "client-partner-credentials-dialog": ClientPartnerCredentialsDialogComponent,
    "client-partner-password-dialog": ClientPartnerPasswordDialogComponent,
    "client-partner-link-dialog": ClientPartnerLinkDialogComponent,
    "client-partner-contacts-dialog": ClientPartnerContactsDialogComponent,
    "client-partner-packages": ClientPartnerPackagesComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientPartnerDetail extends Vue {
  @Ref("fieldsDialog") readonly fieldsDialog!: ClientPartnerFieldsDialogComponent;
  @Ref("credentialsDialog") readonly credentialsDialog!: ClientPartnerCredentialsDialogComponent;
  @Ref("passwordDialog") readonly passwordDialog!: ClientPartnerPasswordDialogComponent;
  @Ref("linkDialog") readonly linkDialog!: ClientPartnerLinkDialogComponent;
  @Ref("contactsDialog") readonly contactsDialog!: ClientPartnerContactsDialogComponent;
  @Ref("confirm") readonly confirmDialog!: ConfirmationDialogComponent;

  partner: ClientPartner | null = null;
  isLoading = true;
  loadError = false;
  isResettingPassword = false;
  isUnlinking = false;

  /** Row icons are primary blue (ui-conventions.md, "Accent or primary goes by where the control sits"). */
  readonly editIconColor = COLOR_ALL_DEVICES_EDIT_ICON;

  get clientId(): string {
    const value = this.$route.query.clientId;
    return typeof value === "string" ? value : "";
  }

  get partnerId(): string {
    const value = this.$route.query.partnerId;
    return typeof value === "string" ? value : "";
  }

  /** Opened from the Externe Systeme list (`from=list`), so back and the title lead there, not to the client. */
  get openedFromList(): boolean {
    return this.$route.query.from === "list";
  }

  /** "Externe Systeme > X360" from the list, else "Mandanten > client > Externe Systeme > X360" (ui-conventions.md). */
  setTitle(name: string | null) {
    if (this.openedFromList) {
      ToolbarContentModule.setToolbarTitle([this.t(`${PARTNERS}.title`), name ?? ""].filter((part) => part !== "").join(" > "));
    } else if (name) {
      setClientPageToolbarTitle(this.clientId, this.t(`${PARTNERS}.title`), name);
    } else {
      setClientPageToolbarTitle(this.clientId, this.t(`${PARTNERS}.title`));
    }
  }

  /** Changed only on the owner's page, by someone who may change clients (not the shared login). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser()) && this.partner?.editable === true;
  }

  private t(key: string, values?: unknown[]): string {
    return this.$t(key, values as never).toString();
  }

  private yesNo(value: boolean): string {
    return this.t(value ? "common.yes" : "common.no");
  }

  get cards(): PartnerCard[] {
    const partner = this.partner;
    if (!partner) {
      return [];
    }
    const connection = partner.connection;
    const signInLines = connection.signIn === null
      ? [{ label: this.t(`${PARTNERS}.fields.signIn`), value: this.t(`${PARTNERS}.signIn.NONE`) }]
      : [
        { label: this.t(`${PARTNERS}.fields.signIn`), value: this.t(`${PARTNERS}.signIn.${connection.signIn}`) },
        ...(["email", "clientId", "authUrl", "tokenUrl"] as const)
          .filter((field) => connection.identifiers[field] !== null)
          .map((field) => ({ label: this.t(`${PARTNERS}.credentials.${field}`), value: connection.identifiers[field] as string })),
        ...(["apiKey", "password"] as const)
          .filter((field) => connection.signIn !== "API_KEY" || field === "apiKey")
          .filter((field) => connection.signIn !== "EASY_TEC" || field === "password")
          .map((field) => ({
            label: this.t(`${PARTNERS}.credentials.${field}`),
            value: this.t(`${PARTNERS}.credentials.${connection.secretsSet[field] ? "set" : "missing"}`),
          })),
      ];

    return [
      {
        group: "PARTNER_STATUS",
        title: this.t(`${PARTNERS}.cards.status`),
        description: this.t(`${PARTNERS}.statusDescriptions.${partner.status}`),
        lines: [{ label: this.t(`${PARTNERS}.fields.integrationStatus`), value: this.t(`${PARTNERS}.statuses.${partner.status}`) }],
      },
      {
        group: "PARTNER_CONNECTION",
        title: this.t(`${PARTNERS}.cards.connection`),
        lines: [
          { label: this.t(`${PARTNERS}.fields.baseUrl`), value: connection.baseUrl ?? this.t(`${PARTNERS}.noAddress`) },
          ...signInLines,
          ...PARTNER_ENDPOINT_FIELDS.map((field) => ({
            label: this.t(`${PARTNERS}.endpoints.${field}`),
            value: connection.endpoints[field] ?? this.t(`${PARTNERS}.defaultEndpoint`, [DEFAULT_ENDPOINTS[field]]),
          })),
        ],
      },
      {
        group: "PARTNER_ACCEPTS",
        title: this.t(`${PARTNERS}.cards.accepts`, [partner.name]),
        lines: PARTNER_ACCEPTS_FIELDS.map((field) => ({ label: this.t(`${PARTNERS}.accepts.${field}`), value: this.yesNo(partner.accepts[field]) })),
      },
      {
        group: "PARTNER_BEHAVIOUR",
        title: this.t(`${PARTNERS}.cards.behaviour`),
        lines: [
          { label: this.t(`${PARTNERS}.fields.system`), value: this.t(partnerSystemLabelKey(partner.system)) },
          { label: this.t(`${PARTNERS}.behaviour.appointmentFieldSwap`), value: this.yesNo(partner.behaviour.appointmentFieldSwap) },
          {
            label: this.t(`${PARTNERS}.behaviour.incomingContactPerson0`),
            value: partner.behaviour.incomingContactPerson0 === null
              ? this.t(`${PARTNERS}.byRoute`)
              : this.yesNo(partner.behaviour.incomingContactPerson0),
          },
          { label: this.t(`${PARTNERS}.behaviour.sendsLatestProjectStatus`), value: this.yesNo(partner.behaviour.sendsLatestProjectStatus) },
          ...(partner.mayBeTestSystem || partner.behaviour.isTestSystem
            ? [{ label: this.t(`${PARTNERS}.behaviour.isTestSystem`), value: this.yesNo(partner.behaviour.isTestSystem) }]
            : []),
        ],
      },
    ];
  }

  async onEdit(group: PartnerChangeGroup) {
    if (!this.partner) {
      return;
    }
    this.apply(await this.fieldsDialog.show(this.clientId, this.partner, group));
  }

  async onEditContacts() {
    if (!this.partner) {
      return;
    }
    this.apply(await this.contactsDialog.show(this.clientId, this.partner));
  }

  /** "max@partner.de · +49 721 123", "–" for neither. */
  contactDetails(contact: PartnerContact): string {
    return [contact.email, contact.phone].filter((part) => part !== null).join(" · ") || "–";
  }

  /** A check's line; the sign-in with when it last happened. */
  goLiveText(check: GoLiveCheck): string {
    const label = this.t(`${PARTNERS}.goLive.checks.${check.key}`);
    if (check.key !== "SIGNED_IN" || check.atMillis === null) {
      return label;
    }
    const date = new Date(check.atMillis);
    return this.t(`${PARTNERS}.goLive.lastSignIn`, [label, `${formatDateToDayMonthYear(date)} ${formatDateToHoursAndMinuteString(date)}`]);
  }

  async onEditCredentials() {
    if (!this.partner) {
      return;
    }
    this.apply(await this.credentialsDialog.show(this.clientId, this.partner));
  }

  async onLink() {
    if (!this.partner) {
      return;
    }
    this.apply(await this.linkDialog.show(this.clientId, this.partner));
  }

  /** Removing a link takes the member's access to this system away at once, so it asks first. */
  async onUnlink(client: ClientPartnerLinkClient) {
    const partner = this.partner;
    if (!partner) {
      return;
    }
    const confirmed = await this.confirmDialog.show({
      title: this.t(`${PARTNERS}.links.unlinkTitle`),
      message: this.t(`${PARTNERS}.links.unlinkMessage`, [client.name, partner.name]),
      acceptMessage: this.t(`${PARTNERS}.links.unlink`),
      rejectMessage: this.t("common.back"),
    });
    if (!confirmed) {
      return;
    }
    this.isUnlinking = true;
    try {
      this.partner = await setClientPartnerLink(this.clientId, partner.id, client.id, false);
      SnackbarModule.showSuccess({ key: `${PARTNERS}.links.unlinked` });
    } catch (error) {
      SnackbarModule.showError({ key: partnerErrorKey(error) });
    } finally {
      this.isUnlinking = false;
    }
  }

  /** NEUES PASSWORT: the old one stops working at once, so it asks first; the new one is shown once. */
  async onNewPassword() {
    const partner = this.partner;
    if (!partner) {
      return;
    }
    const confirmed = await this.confirmDialog.show({
      title: this.t(`${PARTNERS}.access.newPasswordTitle`),
      message: this.t(`${PARTNERS}.access.newPasswordMessage`, [partner.name]),
      acceptMessage: this.t(`${PARTNERS}.access.newPassword`),
      rejectMessage: this.t("common.back"),
    });
    if (!confirmed) {
      return;
    }
    this.isResettingPassword = true;
    try {
      const password = await resetClientPartnerPassword(this.clientId, partner.id);
      await this.passwordDialog.show(partner.name, partner.access?.email ?? null, password);
    } catch (error) {
      SnackbarModule.showError({ key: partnerErrorKey(error) });
    } finally {
      this.isResettingPassword = false;
    }
  }

  /** A save shows its result in place; a change made meanwhile reloads in the background (no spinner). */
  apply(result: PartnerDialogResult) {
    if (result === "CHANGED_MEANWHILE") {
      this.load(false);
    } else if (result) {
      this.partner = result;
    }
  }

  onBack() {
    if (this.openedFromList) {
      router.push({ name: ComponentName.EXTERNAL_SYSTEM_LIST });
    } else {
      router.push({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: this.clientId } });
    }
  }

  async load(showLoading = true) {
    if (showLoading) {
      this.isLoading = true;
    }
    this.loadError = false;
    try {
      this.partner = await getClientPartner(this.clientId, this.partnerId);
      this.setTitle(this.partner.name);
    } catch {
      // Shown as the page's failure state; the back button is the way out.
      this.loadError = true;
    }
    this.isLoading = false;
  }

  mounted() {
    this.setTitle(null);
    this.load();
  }
}
